import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { remindCobroNote } from '../../../../api/collections';
import { showToast } from '../../../../utils/showToast';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import useCollectionsSetup from '../hooks/useCollectionsSetup';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import useReminderMessagePreview from '../hooks/useReminderMessagePreview';
import useReminderTemplates from '../hooks/useReminderTemplates';
import DeliveryStatusBanner from './DeliveryStatusBanner';
import ReminderSendPreview from './ReminderSendPreview';
import ReminderTemplateFields from './ReminderTemplateFields';
import WhatsappNumberSelect, { SelectedNumberWarnings } from './WhatsappNumberSelect';
import {
  REMINDER_CHANNELS,
  apiErrorCode,
  defaultWhatsappNumber,
  hoursCeil,
  isSavedWhatsappMissing,
  selectedChannels,
  whatsappNumberOptions,
} from '../utils/collectionReminders';
import {
  expectedTemplateVersions,
  invalidTemplateChannels,
  isTemplateChanged,
  operatorDefaultTemplateIds,
  reminderSendErrorText,
  requestedTemplateIds,
} from '../utils/reminderTemplates';

/**
 * Por qué un canal no se puede usar ahora (clave i18n + datos), o null si está disponible.
 * @param {object|undefined} delivery `delivery` de GET settings
 * @param {{ remainingMs: number } | null} cooldown enfriamiento vigente del canal
 */
export function channelBlock(delivery, channel, cooldown) {
  const configured = !delivery || (channel === 'email' ? delivery.emailConfigured : delivery.whatsappConfigured);
  if (!configured) return { key: 'No está configurado en este entorno.' };
  if (cooldown) {
    return {
      key: 'Ya se envió uno a este comercio por este canal: se podrá reenviar en {{horas}} h.',
      params: { horas: hoursCeil(cooldown.remainingMs) },
    };
  }
  return null;
}

/**
 * "Enviar recordatorio" de UNA nota, ahora: elegir canales (y número si hay varios) y
 * encolar. Billing responde 202 y el envío sale en un ciclo corto del worker; el resultado
 * aparece en el historial de la nota.
 *
 * La clave de idempotencia se genera al ABRIR el formulario y se reutiliza en los reintentos
 * del clic (el axios del panel corta a los 60 s): un segundo clic tras un timeout devuelve
 * lo ya encolado en vez de mandar otro mensaje. Cuándo cambia: ver useIdempotencyKey.
 *
 * Con billing con plantillas (los settings traen `defaultTemplateIds`) se elige la plantilla
 * de cada canal, arrancando en la por defecto, y al lado se ve el mensaje exacto que saldría
 * hoy. La plantilla viaja explícita en `templateIds`, aunque sea la por defecto, y con ella
 * la versión que mostró la vista previa (`expectedTemplateVersions`): si otra persona la
 * editó en el medio, billing responde 409 `template_changed` sin encolar nada, la vista
 * previa se recarga y se pide confirmar de nuevo.
 *
 * La plantilla por defecto sale de los settings recargados en ESTA apertura: el modal queda
 * montado y, al reabrirlo, los de la anterior pueden estar viejos.
 *
 * @param {{ open: boolean, onClose: () => void, note: object,
 *   cooldown: { email: object|null, whatsapp: object|null }, onQueued?: () => void }} props
 */
export default function SendReminderModal({ open, onClose, note, cooldown, onQueued }) {
  const { t } = useTranslation();
  const setup = useCollectionsSetup({ enabled: open, withIntegrations: true });
  const templates = useReminderTemplates({ enabled: open });
  const idempotency = useIdempotencyKey(open);
  const keyError = idempotency.keyError;
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  // Canales cuya plantilla rechazó billing al enviar (se limpia al editar).
  const [rejectedTemplates, setRejectedTemplates] = useState([]);
  // Billing avisó que una plantilla cambió desde la vista previa: se confirma de nuevo con
  // la vista previa recargada (no antes de que llegue).
  const [templateChanged, setTemplateChanged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(null);
    setError(null);
    setRejectedTemplates([]);
    setTemplateChanged(false);
  }, [open]);

  const view = setup.view;
  const delivery = view?.delivery;
  const templateDefaults = useMemo(() => operatorDefaultTemplateIds(view?.settings), [view]);
  const savedWhatsappId = view?.settings?.autoReminders?.whatsappIntegrationId ?? null;
  const waOptions = useMemo(
    () => whatsappNumberOptions(setup.integrations, savedWhatsappId),
    [setup.integrations, savedWhatsappId],
  );
  const blocks = useMemo(
    () =>
      Object.fromEntries(
        REMINDER_CHANNELS.map((c) => [c, channelBlock(delivery, c, cooldown?.[c] ?? null)]),
      ),
    [delivery, cooldown],
  );

  // Por defecto, todos los canales disponibles, el número guardado en la configuración
  // (WhatsApp tildado solo si hay un número por defecto: si no, hay que elegirlo) y la
  // plantilla por defecto de cada canal. Recién con los settings de esta apertura: mientras
  // recargan, `view` sigue siendo el de la anterior.
  useEffect(() => {
    if (!open || !view || form || setup.loading) return;
    const whatsappIntegrationId = defaultWhatsappNumber(waOptions, savedWhatsappId);
    setForm({
      channels: { email: !blocks.email, whatsapp: !blocks.whatsapp && !!whatsappIntegrationId },
      whatsappIntegrationId,
      templateIds: templateDefaults,
    });
  }, [open, view, form, setup.loading, blocks, waOptions, savedWhatsappId, templateDefaults]);

  const selectableNumbers = waOptions.filter((o) => o.selectable);
  // El número configurado ya no aparece (borrado o inactivo): WhatsApp arranca sin tildar en
  // vez de salir por otro número. Si fallaron los números, no se sabe: no se afirma nada.
  const savedNumberMissing =
    !setup.integrationsError && isSavedWhatsappMissing(waOptions, savedWhatsappId);
  // Canales que de verdad se mandan. Los valores por defecto se fijan una vez: si un canal
  // entra en enfriamiento después (el historial llegó tarde), se ve destildado y no se envía.
  const channels = form
    ? {
        email: !!form.channels.email && !blocks.email,
        whatsapp: !!form.channels.whatsapp && !blocks.whatsapp,
      }
    : null;

  const noteIds = useMemo(() => (note?._id ? [note._id] : []), [note?._id]);
  const preview = useReminderMessagePreview({
    noteIds,
    channels,
    templateIds: form?.templateIds,
    enabled: open && !!form && !!templateDefaults,
  });
  // Un 400 de la vista previa por una plantilla archivada o de otro canal: se recarga el
  // catálogo para que el selector la muestre como no disponible.
  const previewRejected = invalidTemplateChannels(preview.error).length > 0;
  const reloadTemplates = templates.reload;
  useEffect(() => {
    if (previewRejected) reloadTemplates();
  }, [previewRejected, reloadTemplates]);
  const invalidTemplates = [
    ...new Set([...rejectedTemplates, ...(preview.pending ? [] : invalidTemplateChannels(preview.error))]),
  ];

  // Todo cambio (también la plantilla) pasa por acá: tras un rechazo de billing es una
  // intención nueva y lleva clave nueva (ver useIdempotencyKey).
  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setError(null);
    setRejectedTemplates([]);
    setTemplateChanged(false);
    idempotency.onEdited();
  };

  const handleSubmit = async () => {
    if (selectedChannels(channels).length === 0) {
      setError(t('Elegí al menos un canal (correo o WhatsApp).'));
      return;
    }
    if (channels.whatsapp && !form.whatsappIntegrationId) {
      setError(t('Elegí el número de WhatsApp desde el que se envían los recordatorios.'));
      return;
    }
    const body = { channels, idempotencyKey: idempotency.key };
    if (channels.whatsapp) body.whatsappIntegrationId = form.whatsappIntegrationId;
    const templateIds = requestedTemplateIds(channels, form.templateIds);
    if (templateIds) body.templateIds = templateIds;
    const expectedVersions = expectedTemplateVersions(channels, form.templateIds, preview.data?.items);
    if (expectedVersions) body.expectedTemplateVersions = expectedVersions;

    setSubmitting(true);
    setError(null);
    setTemplateChanged(false);
    try {
      await remindCobroNote(note._id, body);
      showToast(t('Recordatorio en cola: sale en unos segundos.'), 'success');
      onQueued?.();
      onClose();
    } catch (err) {
      // Queda abierto. Si no se sabe si se encoló, con la MISMA clave: reintentar devuelve lo
      // ya encolado.
      idempotency.onRequestFailed(err);
      // Un intento anterior (con otros canales o plantillas) sí quedó en cola: se refresca el
      // historial.
      if (apiErrorCode(err) === 'idempotency_key_reused') onQueued?.();
      const rejected = invalidTemplateChannels(err);
      if (rejected.length > 0) {
        setRejectedTemplates(rejected);
        reloadTemplates();
      }
      if (isTemplateChanged(err)) {
        // No se encoló nada: con la versión nueva a la vista es otra intención (clave nueva
        // si la anterior fue rechazada; se conserva si un intento previo quedó incierto).
        setTemplateChanged(true);
        idempotency.onEdited();
        preview.refresh();
      }
      setError(reminderSendErrorText(err, t, 'No se pudo enviar el recordatorio.'));
    } finally {
      setSubmitting(false);
    }
  };

  let body;
  if (!form && setup.loading) {
    body = (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/3" />
      </div>
    );
  } else if (setup.error && !view) {
    body = (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>
          {setup.error.status === 403
            ? t('Tu rol no tiene permiso para gestionar la cobranza.')
            : setup.error.message || t('No se pudo cargar la configuración de cobranza.')}
        </span>
        {setup.error.status !== 403 && (
          <Button size="sm" variant="outline" onClick={setup.reload}>
            {t('Reintentar')}
          </Button>
        )}
      </div>
    );
  } else if (form) {
    const fields = (
      <div className="min-w-0 space-y-4">
        <DeliveryStatusBanner delivery={delivery} channels={selectedChannels(channels)} />
        {keyError && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {t('Este navegador no permite generar la clave del envío. Actualizalo o probá con otro.')}
          </p>
        )}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-fg">{t('Canales')}</legend>
          {REMINDER_CHANNELS.map((channel) => {
            const block = blocks[channel];
            return (
              <label
                key={channel}
                className={`flex items-start gap-2 text-sm text-fg ${block ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-brand-500"
                  checked={channels[channel]}
                  disabled={!!block}
                  onChange={(e) => update({ channels: { ...form.channels, [channel]: e.target.checked } })}
                />
                <span>
                  {channel === 'email' ? t('Correo') : t('WhatsApp')}
                  {block && <span className="block text-xs text-fg-muted">{t(block.key, block.params)}</span>}
                  {channel === 'whatsapp' && !block && savedNumberMissing && !channels.whatsapp && (
                    <span className="block text-xs text-status-warning">
                      {t('El número de WhatsApp configurado no está disponible. Para enviar por WhatsApp, tildalo y elegí otro número.')}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>

        {channels.whatsapp &&
          (selectableNumbers.length === 1 && form.whatsappIntegrationId === selectableNumbers[0].id ? (
            <div className="space-y-2">
              <p className="text-xs text-fg-muted">
                {t('WhatsApp sale desde {{numero}}.', {
                  numero: [selectableNumbers[0].label, selectableNumbers[0].phone].filter(Boolean).join(' · '),
                })}
              </p>
              <SelectedNumberWarnings option={selectableNumbers[0]} />
            </div>
          ) : (
            <WhatsappNumberSelect
              integrations={setup.integrations}
              loading={setup.integrationsLoading}
              error={setup.integrationsError}
              onRetry={setup.reloadIntegrations}
              value={form.whatsappIntegrationId}
              savedId={savedWhatsappId}
              onChange={(id) => update({ whatsappIntegrationId: id })}
            />
          ))}

        {templateDefaults && (
          <ReminderTemplateFields
            channels={selectedChannels(channels)}
            value={form.templateIds}
            onChange={(channel, templateId) => update({ templateIds: { ...form.templateIds, [channel]: templateId } })}
            templates={templates}
            defaults={templateDefaults}
            invalidChannels={invalidTemplates}
            disabled={submitting}
          />
        )}

        <p className="text-xs text-fg-muted">
          {t('Se envía aunque el comercio tenga pausados los automáticos. Máximo un recordatorio cada 24 h por canal para cada comercio.')}
        </p>

        {error && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
    // Sin plantillas en billing no hay vista previa: queda solo el formulario.
    body = templateDefaults ? (
      <div className="grid gap-5 md:grid-cols-2">
        {fields}
        <ReminderSendPreview preview={preview} />
      </div>
    ) : (
      fields
    );
  }

  const blocked =
    keyError ||
    !form ||
    (delivery && !delivery.schedulerEnabled) ||
    selectedChannels(channels).length === 0 ||
    (templateChanged && preview.pending);

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      size="lg"
      title={t('Enviar recordatorio')}
      description={t('Nota #{{folio}}', { folio: note?.folio ?? '—' })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('Cancelar')}
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={blocked} iconLeft={<Send className="h-4 w-4" />}>
            {t('Enviar')}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
